// ============================================================================
// format.ts — ЕДИНЫЙ механизм форматирования вывода значений (SRP, SSoT).
// SSoT — настройки на СЕРВЕРЕ:
//   • metadata_fields.format / metadata_tabular_columns.format (формат реквизита);
//   • config.timezone (часовой пояс конфигурации);
//   • config.format_* (ГЛОБАЛЬНЫЕ форматы по умолчанию для основных типов —
//     применяются, если реквизит НЕ переопределил; как в 1С).
// Клиент ТОЛЬКО форматирует — единый модуль используется ВЕЗДЕ:
// списки, карточки, табличные части, формы, отчёты.
//
// Спецификация формата (строка; несколько правил через '|'):
//   date:dd.MM.yyyy          — дата по шаблону (dd, MM, yyyy/yy, HH, mm, ss)
//   datetime:dd.MM.yyyy HH:mm— то же, что date:
//   time:HH:mm               — время
//   number:N                 — число с N знаками после запятой
//   number:N;group           — с группировкой разрядов
//   empty:dash               — пустое значение выводить как «—»
//   bool:yesno               — булево: Да/Нет
//   bool:check               — булево: ✓/—
//   text:upper|lower         — регистр строки
//   '' (пусто)               — глобальный формат по умолчанию из конфигурации
//                              (config.format_*), иначе по типу:
//                              date → dd.MM.yyyy, datetime → dd.MM.yyyy HH:mm,
//                              time → HH:mm, всё в часовом поясе конфигурации.
// Хранение — ВСЕГДА полные данные (даты с временем, UTC) — форматирование
// только на выводе (кейс: храним полные, показываем дату).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('format');

let cachedTz = '';            // '' = локальный пояс
let cachedFormats: Record<string, string> = {};
let tzPromise: Promise<void> | null = null;
const subscribers = new Set<() => void>();

/** Загрузка часового пояса и глобальных форматов конфигурации (SSoT — сервер).
 *  ГЕЙТ базы: форматы принадлежат базе — без baseSlug запрос невалиден
 *  («no base selected»); смена базы → перезагрузка (resetConfigFormats). */
export function loadConfigFormats(): Promise<void> {
  const slug = useCenterStore.getState().baseSlug;
  if (!slug) {
    // Лаунчер/смена базы: кэш недействителен — следующий вызов после выбора
    // базы загрузит заново (не зависаем на пустом promise)
    if (tzPromise) {
      tzPromise = null;
      cachedTz = '';
      cachedFormats = {};
    }
    return Promise.resolve();
  }
  if (tzPromise) return tzPromise;
  tzPromise = getSDK().config
    .getConfigInfo({})
    .then((r: any) => {
      cachedTz = String((r as any)?.timezone || '');
      cachedFormats = {
        date: String((r as any)?.formatDate || ''),
        datetime: String((r as any)?.formatDatetime || ''),
        time: String((r as any)?.formatTime || ''),
        number: String((r as any)?.formatNumber || ''),
        empty: String((r as any)?.formatEmpty || ''),
        boolean: String((r as any)?.formatBoolean || ''),
      };
      subscribers.forEach((cb) => cb());
    })
    .catch((e: any) => {
      cachedTz = '';
      cachedFormats = {};
      tzPromise = null; // ошибка НЕ кэшируется навсегда — повтор при следующем доступе
      log.debug('config formats load failed', e);
    });
  return tzPromise;
}

/** Синхронный доступ к уже загруженному поясу ('' — локальный). */
export function getConfigTimezoneSync(): string {
  return cachedTz;
}

/** Сброс кэша после сохранения конфигурации. */
export function resetConfigFormats(): void {
  cachedTz = '';
  cachedFormats = {};
  tzPromise = null;
}

/** Подписка на загрузку/смену настроек конфигурации (ре-рендер). */
export function subscribeConfigFormats(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0');
}

interface Parts { y: number; M: number; d: number; h: number; m: number; s: number }

function partsForTz(date: Date, tz: string): Parts {
  if (!tz || tz.toLowerCase() === 'local') {
    return { y: date.getFullYear(), M: date.getMonth() + 1, d: date.getDate(), h: date.getHours(), m: date.getMinutes(), s: date.getSeconds() };
  }
  const off = /^UTC([+-])(\d{1,2})(?::?(\d{2}))?$/i.exec(tz.trim());
  if (off) {
    const sign = off[1] === '-' ? -1 : 1;
    const mins = sign * (parseInt(off[2], 10) * 60 + parseInt(off[3] || '0', 10));
    const shifted = new Date(date.getTime() + mins * 60000);
    return { y: shifted.getUTCFullYear(), M: shifted.getUTCMonth() + 1, d: shifted.getUTCDate(), h: shifted.getUTCHours(), m: shifted.getUTCMinutes(), s: shifted.getUTCSeconds() };
  }
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
    const p: Record<string, string> = {};
    for (const x of dtf.formatToParts(date)) p[x.type] = x.value;
    return { y: Number(p.year), M: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, m: Number(p.minute), s: Number(p.second) };
  } catch {
    return { y: date.getFullYear(), M: date.getMonth() + 1, d: date.getDate(), h: date.getHours(), m: date.getMinutes(), s: date.getSeconds() };
  }
}

/** Парсинг даты (JS-форматы + Go/SQLite "2006-01-02 15:04:05 +0000 UTC"). */
function parseDateValue(raw: string): Date | null {
  // Go/SQLite: "2026-08-07 02:29:03 +0000 UTC" / "+0300 MSK" (секунды с долями)
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.\d+)? ([+-]\d{4}) [A-Za-z]+$/.exec(raw);
  if (m) {
    const off = m[3];
    const offMin = (parseInt(off.slice(0, 3), 10) * 60 + parseInt(off.slice(3), 10)) * (off[0] === '-' ? -1 : 1);
    const d = new Date(m[1] + 'T' + m[2] + 'Z');
    if (!Number.isNaN(d.getTime())) {
      d.setUTCMinutes(d.getUTCMinutes() + offMin);
    }
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Форматирование даты/времени по шаблону (dd/MM/yyyy/yy/HH/mm/ss) в поясе конфигурации. */
export function formatDate(value: unknown, pattern?: string, tz?: string): string {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '<nil>') return '';
  const date = parseDateValue(raw);
  if (!date) return raw;
  const t = tz ?? cachedTz;
  const p = partsForTz(date, t);
  const pat = (pattern || '').trim() || 'dd.MM.yyyy HH:mm';
  return pat
    .replace(/dd/g, pad(p.d))
    .replace(/MM/g, pad(p.M))
    .replace(/yyyy/g, pad(p.y, 4))
    .replace(/yy/g, pad(p.y % 100))
    .replace(/HH/g, pad(p.h))
    .replace(/mm/g, pad(p.m))
    .replace(/ss/g, pad(p.s));
}

/** Форматирование числа: '2' — знаков; ';group' — группировка. */
export function formatNumber(value: unknown, spec?: string): string {
  const num = Number(value);
  if (value === null || value === undefined || value === '' || value === '<nil>' || Number.isNaN(num)) {
    return value === null || value === undefined || value === '' || value === '<nil>' ? '' : String(value);
  }
  const s = (spec || '').trim();
  const [digitsPart, ...rest] = s.split(';');
  const group = rest.some((r) => r.trim() === 'group');
  let digits = 0;
  const dp = Number(digitsPart);
  if (!Number.isNaN(dp)) digits = dp;
  try {
    return num.toLocaleString('ru-RU', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: group,
    });
  } catch {
    return num.toFixed(digits);
  }
}

/** Глобальный формат по умолчанию для типа (config.format_*).
 * ЕДИНАЯ настройка «Формат дат по умолчанию» (config.format_date) применяется
 * ко ВСЕМ видам дат (date/timestamp/datetime/time): выводим только дату
 * без времени — если так задано в конфигурации (как в 1С). */
function defaultForType(dt: string): string {
  if (dt === 'date' || dt === 'time' || dt === 'timestamp' || dt === 'datetime') {
    return cachedFormats.date || (dt === 'time' ? 'time:HH:mm' : dt === 'timestamp' || dt === 'datetime' ? 'date:dd.MM.yyyy HH:mm:ss' : 'date:dd.MM.yyyy');
  }
  if (dt === 'number') return cachedFormats.number || '';
  if (dt === 'boolean') return cachedFormats.boolean || '';
  return '';
}

/**
 * УНИВЕРСАЛЬНОЕ форматирование значения (SSoT — сервер).
 * Правила формата разделяются '|': 'date:dd.MM.yyyy|empty:dash'.
 */
export function formatValue(value: unknown, format?: string, dataType?: string): string {
  const isNil = value === null || value === undefined || value === '' || value === '<nil>';
  if (isNil) {
    const fmt = format || '';
    const emptyRule = fmt.split('|').find((r) => r.startsWith('empty:'));
    const globalEmpty = cachedFormats.empty || '';
    if (emptyRule === 'empty:dash' || globalEmpty === 'empty:dash') return '—';
    return '';
  }

  const fmt = (format || '').trim();
  const dt = (dataType || '').toLowerCase();
  const rules = fmt.split('|').map((r) => r.trim()).filter(Boolean);
  // Формат не задан — глобальный дефолт по типу (как в 1С: конфигурация задаёт,
  // реквизит переопределяет)
  const effectiveRules = rules.length > 0 ? rules : [defaultForType(dt)].filter(Boolean);
  if (effectiveRules.length === 0) return String(value);

  for (const rule of effectiveRules) {
    if (rule.startsWith('date:') || rule.startsWith('datetime:')) {
      return formatDate(value, rule.slice(rule.indexOf(':') + 1));
    }
    if (rule.startsWith('time:')) {
      return formatDate(value, rule.slice('time:'.length));
    }
    if (rule.startsWith('number:')) {
      return formatNumber(value, rule.slice('number:'.length));
    }
    if (rule.startsWith('bool:')) {
      const v = String(value);
      const isTrue = v === 'true' || v === '1' || v === '1.0';
      const mode = rule.slice('bool:'.length).trim();
      if (mode === 'yesno') return isTrue ? 'Да' : 'Нет';
      return isTrue ? '✓' : '—';
    }
    if (rule.startsWith('text:')) {
      const mode = rule.slice('text:'.length).trim();
      const str = String(value);
      if (mode === 'upper') return str.toUpperCase();
      if (mode === 'lower') return str.toLowerCase();
      return str;
    }
  }
  // По умолчанию — по типу (даты — в поясе конфигурации)
  if (dt === 'date' || dt === 'timestamp' || dt === 'datetime') return formatDate(value);
  if (dt === 'time') return formatDate(value, 'HH:mm');
  return String(value);
}

/** Подсказка для модального редактора (не текстом, а выбором). */
export function formatHuman(format?: string): string {
  const rules = (format || '').split('|').filter(Boolean);
  if (rules.length === 0) return 'По умолчанию (конфигурация)';
  const labels: Record<string, string> = {
    'date:dd.MM.yyyy': 'Дата',
    'date:dd.MM.yyyy HH:mm': 'Дата и время',
    'date:dd.MM.yyyy HH:mm:ss': 'Дата и время (сек)',
    'time:HH:mm': 'Время',
    'empty:dash': 'Пустое: —',
    'bool:yesno': 'Да/Нет',
    'bool:check': '✓/—',
  };
  return rules.map((r) => labels[r] || r).join(' · ');
}
